// Contract Templates for different project types

const contractTemplates = {
  technical: {
    title: 'Technical Services Agreement',
    sections: {
      scope: {
        title: 'Scope of Work',
        content: 'The Service Provider agrees to deliver technical services as described in the project specifications including system design, implementation, testing, and deployment.'
      },
      deliverables: {
        title: 'Deliverables',
        content: 'Key deliverables include: source code, technical documentation, system architecture diagrams, unit tests, and deployment guides.'
      },
      timeline: {
        title: 'Timeline and Milestones',
        content: 'The project will follow an agreed-upon timeline with defined milestones. Any delays exceeding 2 weeks require written notification to the client.'
      },
      support: {
        title: 'Support and Maintenance',
        content: 'Post-deployment support includes 30 days of bug fixes and performance optimization at no additional cost. Extended support available at hourly rates.'
      },
      ip_rights: {
        title: 'Intellectual Property Rights',
        content: 'All custom code and documentation developed under this contract shall remain the property of the client upon full payment.'
      },
      confidentiality: {
        title: 'Confidentiality',
        content: 'Both parties agree to maintain strict confidentiality of all proprietary information shared during the engagement.'
      },
      payment_terms: {
        title: 'Payment Terms',
        content: 'Invoices will be issued as per agreed milestones. Payment is due within 30 days of invoice date.'
      },
      termination: {
        title: 'Termination',
        content: 'Either party may terminate this agreement with 15 days written notice. Client is responsible for payment of work completed to date.'
      }
    }
  },
  data_engineering: {
    title: 'Data Engineering Services Agreement',
    sections: {
      scope: {
        title: 'Scope of Work',
        content: 'The Service Provider agrees to design and implement data engineering solutions including data pipeline development, ETL processes, database optimization, and data warehouse setup.'
      },
      deliverables: {
        title: 'Deliverables',
        content: 'Key deliverables include: data pipeline architecture, ETL scripts, schema design, performance reports, monitoring dashboards, and comprehensive data documentation.'
      },
      data_handling: {
        title: 'Data Handling and Security',
        content: 'All data will be handled according to industry best practices and applicable data protection regulations (GDPR, CCPA, etc.). Data encryption and secure access controls will be implemented.'
      },
      quality_assurance: {
        title: 'Quality Assurance',
        content: 'All data pipelines will undergo rigorous testing including data validation, error handling, and performance benchmarking before deployment.'
      },
      timeline: {
        title: 'Timeline and Milestones',
        content: 'The project will follow an agreed-upon timeline with defined milestones for each data engineering phase (design, implementation, testing, deployment).'
      },
      ip_rights: {
        title: 'Intellectual Property Rights',
        content: 'All custom scripts, algorithms, and data schemas developed remain the property of the client upon full payment.'
      },
      maintenance: {
        title: 'Maintenance and Support',
        content: 'Includes 60 days of performance monitoring and optimization. Extended support and SLA-based services available separately.'
      },
      payment_terms: {
        title: 'Payment Terms',
        content: 'Invoices issued based on project phases. Payment due within 30 days of invoice date.'
      }
    }
  },
  web_application: {
    title: 'Web Application Development Agreement',
    sections: {
      scope: {
        title: 'Scope of Work',
        content: 'The Service Provider agrees to develop a web application including frontend design, backend development, database integration, API development, and deployment to production.'
      },
      deliverables: {
        title: 'Deliverables',
        content: 'Key deliverables include: responsive web interface, RESTful APIs, database schema, admin dashboard, user documentation, source code repository, and deployment guides.'
      },
      technology_stack: {
        title: 'Technology Stack',
        content: 'Modern technology stack including React/Vue/Angular for frontend, Node.js/Django/Laravel for backend, and MySQL/PostgreSQL for database as agreed upon.'
      },
      responsive_design: {
        title: 'Responsive Design',
        content: 'The application will be fully responsive and compatible with desktop, tablet, and mobile devices. Testing across major browsers (Chrome, Firefox, Safari, Edge) included.'
      },
      testing: {
        title: 'Testing and QA',
        content: 'Comprehensive testing including unit tests, integration tests, end-to-end tests, and user acceptance testing before production deployment.'
      },
      hosting_deployment: {
        title: 'Hosting and Deployment',
        content: 'Initial deployment to client\'s hosting infrastructure or recommended cloud platforms (AWS, Azure, GCP). Deployment guide and training provided.'
      },
      maintenance: {
        title: 'Maintenance and Support',
        content: '30 days of free bug fixes and minor enhancements post-launch. Ongoing support available through separate maintenance agreement.'
      },
      ip_rights: {
        title: 'Intellectual Property Rights',
        content: 'All custom code, design elements, and documentation developed for this project shall be the exclusive property of the client.'
      }
    }
  }
};

export const generateContract = (projectType) => {
  const template = contractTemplates[projectType] || contractTemplates.technical;
  
  return {
    title: template.title,
    sections: template.sections
  };
};

export const generateContractText = (projectType) => {
  const contract = generateContract(projectType);
  let text = `${contract.title}\n\n`;
  
  Object.values(contract.sections).forEach((section) => {
    text += `${section.title}\n${section.content}\n\n`;
  });
  
  return text;
};

export const getContractHTML = (projectType, companyName = 'The Service Provider', clientName = 'The Client') => {
  const contract = generateContract(projectType);
  
  let html = `
    <div class="contract-document">
      <h1 class="contract-title">${contract.title}</h1>
      <p class="contract-date">Effective Date: ${new Date().toLocaleDateString()}</p>
      <p class="contract-parties">
        <strong>Between:</strong> ${companyName} (Service Provider) and ${clientName} (Client)
      </p>
  `;
  
  Object.entries(contract.sections).forEach(([key, section]) => {
    html += `
      <section class="contract-section">
        <h2 class="section-title">${section.title}</h2>
        <p class="section-content">${section.content}</p>
      </section>
    `;
  });
  
  html += `
      <div class="contract-signature">
        <p>By executing this agreement, both parties acknowledge their understanding and acceptance of these terms.</p>
        <div class="signature-block">
          <p>_______________________________</p>
          <p>Service Provider Signature</p>
        </div>
        <div class="signature-block">
          <p>_______________________________</p>
          <p>Client Signature</p>
        </div>
      </div>
    </div>
  `;
  
  return html;
};

export const projectTypes = [
  { value: 'technical', label: 'Technical Services' },
  { value: 'data_engineering', label: 'Data Engineering' },
  { value: 'web_application', label: 'Web Application Development' }
];

export default contractTemplates;
